import { useState, useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, Save, Loader2, ShieldOff, ShieldCheck, Camera, X } from "lucide-react";
import type { Character } from "../services/api";
import { createCharacter, updateCharacter, getCharacter, getCharacters, uploadAvatarAPI, getSettings, type UserSettings } from "../services/api";

interface Props {
  onBack: () => void;
  onCreated: (char: Character) => void;
  onUpdated?: (char: Character) => void;
  editCharacter?: Character | null;
}

const CATEGORIES = [
  { id: "romance", label: "浪漫", emoji: "💕" },
  { id: "fantasy", label: "奇幻", emoji: "🔮" },
  { id: "adventure", label: "冒险", emoji: "⚔️" },
  { id: "scifi", label: "科幻", emoji: "🚀" },
  { id: "anime", label: "动漫", emoji: "✨" },
  { id: "roleplay", label: "角色扮演", emoji: "🎭" },
];

const PERSONALITY_PRESETS = [
  { label: "温柔体贴", value: "温柔、体贴、善解人意" },
  { label: "高冷傲娇", value: "高冷、傲娇、内心温柔" },
  { label: "活泼开朗", value: "活泼、开朗、元气满满" },
  { label: "成熟稳重", value: "成熟、稳重、知性" },
  { label: "神秘优雅", value: "神秘、优雅、知性" },
  { label: "热血豪爽", value: "热血、豪爽、重情重义" },
];

export function CharacterCreator({ onBack, onCreated, onUpdated, editCharacter }: Props) {
  const isEdit = !!editCharacter;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("roleplay");
  const [personality, setPersonality] = useState("");
  const [background, setBackground] = useState("");
  const [scenario, setScenario] = useState("");
  const [greeting, setGreeting] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [allTags, setAllTags] = useState<string[]>([]);
  const [nsfw, setNsfw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [gender, setGender] = useState<"male" | "female">("female");
  const [avatarUrl, setAvatarUrl] = useState("");  // custom uploaded avatar (base64 data URL)
  const [avatarUploading, setAvatarUploading] = useState(false);

  const [currentModel, setCurrentModel] = useState(""); // 后台当前在用模型

  const [loading, setLoading] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState("");

  // Phase 2: 性格滑块状态
  const defaultTraits = {
    shyness: 50,
    warmth: 50,
    assertiveness: 50,
    jealousy: 30,
    humor: 50,
    formality: 50,
  };
  const [traits, setTraits] = useState(defaultTraits);

  // Generate DiceBear avatar URL based on name and gender
  const diceBearAvatar = useMemo(() => {
    if (avatarUrl) return avatarUrl;
    const style = gender === "male" ? "adventurer" : "notionists";
    const seed = name.trim() || "default";
    return `https://api.dicebear.com/7.x/${style}/svg?seed=${encodeURIComponent(seed)}&backgroundColor=845ef7`;
  }, [name, gender, avatarUrl]);

  // 加载后台当前在用模型
  useEffect(() => {
    getSettings()
      .then((s: UserSettings) => setCurrentModel(s.ollama_model || ""))
      .catch(() => {});
  }, []);

  // 加载所有角色的历史标签（去重）
  useEffect(() => {
    getCharacters({})
      .then((chars) => {
        const tagSet = new Set<string>();
        chars.forEach((c) => (c.tags || []).forEach((t: string) => tagSet.add(t)));
        const freq: Record<string, number> = {};
        chars.forEach((c) => (c.tags || []).forEach((t: string) => { freq[t] = (freq[t] || 0) + 1; }));
        setAllTags([...tagSet].sort((a, b) => (freq[b] || 0) - (freq[a] || 0)));
      })
      .catch(() => {});
  }, []);

  // 编辑模式：从 API 获取最新数据填充表单
  useEffect(() => {
    if (editCharacter) {
      setLoading(true);
      getCharacter(editCharacter.id)
        .then((char) => {
          setName(char.name || "");
          setDescription(char.description || "");
          setCategory(char.category || "roleplay");
          setPersonality(char.personality || "");
          setBackground(char.background || "");
          setScenario(char.scenario || "");
          setGreeting(char.greeting || "");
          setSystemPrompt((char as any).system_prompt || "");
          setTags(Array.isArray(char.tags) ? char.tags : []);
          setNsfw(!!char.nsfw);
          setGender((char as any).gender || "female");
          // If avatar is a data URL (custom uploaded), set it
          if ((char as any).avatar && (char as any).avatar.startsWith("data:")) {
            setAvatarUrl((char as any).avatar);
          } else {
            setAvatarUrl("");
          }
          if (char.traits && typeof char.traits === "object" && !Array.isArray(char.traits)) {
            setTraits({ ...defaultTraits, ...char.traits });
          } else {
            setTraits(defaultTraits);
          }
        })
        .catch((e) => {
          console.error("加载角色数据失败:", e);
          setError("加载角色数据失败，请返回重试");
        })
        .finally(() => setLoading(false));
    }
  }, [editCharacter?.id]);

  async function handleAvatarUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setError("图片大小不能超过 2MB");
      return;
    }
    setAvatarUploading(true);
    try {
      const res = await uploadAvatarAPI(file);
      setAvatarUrl(res.url);
    } catch (err: any) {
      setError(err.message || "头像上传失败");
    }
    setAvatarUploading(false);
  }

  function handleAddTag(tagText?: string) {
    const val = (tagText || tagInput).trim();
    if (val && !tags.includes(val) && tags.length < 10) {
      setTags([...tags, val]);
      setTagInput("");
    }
  }

  // 推荐标签：过滤掉已选的，匹配输入框关键词
  const suggestedTags = useMemo(() => {
    const q = tagInput.trim().toLowerCase();
    return allTags
      .filter((t) => !tags.includes(t))
      .filter((t) => !q || t.toLowerCase().includes(q));
  }, [allTags, tags, tagInput]);

  async function handleSave() {
    if (!name.trim()) {
      setError("请输入角色名称");
      return;
    }
    if (!description.trim()) {
      setError("请输入角色描述");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        category,
        personality,
        background,
        scenario,
        greeting: greeting.trim(),
        system_prompt: systemPrompt,
        tags,
        nsfw,
        avatar_seed: name.trim(),
        avatar: avatarUrl,  // custom avatar (base64 data URL) or empty
        gender,
        traits,
      };

      if (isEdit && editCharacter) {
        const char = await updateCharacter(editCharacter.id, payload);
        onUpdated?.(char);
      } else {
        const char = await createCharacter(payload);
        onCreated(char);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : isEdit ? "更新失败" : "创建失败");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="h-full overflow-y-auto bg-slate-950">
      <div className="max-w-2xl mx-auto p-6">
        {/* 顶部 */}
        <div className="flex items-center gap-3 mb-8">
          <button onClick={onBack} className="p-2 rounded-lg hover:bg-slate-800 transition-colors text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-rose-400" />
              {isEdit ? "编辑角色" : "创建角色"}
            </h1>
            <p className="text-xs text-slate-400">{isEdit ? `正在编辑: ${editCharacter?.name}` : "设计一个独一无二的 AI 角色"}</p>
          </div>
        </div>

        {/* 后台当前模型 */}
        {currentModel && (
          <div className="mb-4 p-3 rounded-xl bg-slate-900/60 border border-slate-700 flex items-center gap-2 text-xs">
            <span className="text-slate-500">后台当前模型：</span>
            <span className="text-rose-400 font-semibold">{currentModel}</span>
          </div>
        )}

        {/* 加载中 */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-rose-400 animate-spin mb-3" />
            <p className="text-sm text-slate-400">正在加载角色数据...</p>
          </div>
        )}

        {/* 表单内容 */}
        {!loading && (
        <>

        {error && (
          <div className="mb-6 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-sm text-red-400">
            {error}
          </div>
        )}

        {/* 表单 */}
        <div className="space-y-6">
          {/* 基本信息 */}
          <section className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
            <h2 className="text-sm font-semibold text-slate-200 mb-4">基本信息</h2>

            {/* 头像预览 + 上传 */}
            <div className="flex items-center gap-4 mb-5">
              <div className="relative group">
                <img
                  src={diceBearAvatar}
                  alt="角色头像"
                  className="w-20 h-20 rounded-2xl border-2 border-slate-700 bg-slate-800 object-cover"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={avatarUploading}
                  className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  {avatarUploading ? (
                    <Loader2 className="w-5 h-5 text-white animate-spin" />
                  ) : (
                    <Camera className="w-5 h-5 text-white" />
                  )}
                </button>
                {avatarUrl && (
                  <button
                    onClick={() => setAvatarUrl("")}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-xs hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarUpload}
                />
              </div>
              <div className="flex-1">
                <p className="text-xs text-slate-400 mb-1">角色头像</p>
                <p className="text-[10px] text-slate-500">点击头像上传自定义图片，或根据性别自动生成</p>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => setGender("female")}
                    className={`px-3 py-1 rounded-lg text-xs transition-all ${
                      gender === "female"
                        ? "bg-pink-500/20 text-pink-400 border border-pink-500/30"
                        : "bg-slate-800 text-slate-400 border border-slate-700 hover:text-white"
                    }`}
                  >
                    ♀ 女性
                  </button>
                  <button
                    onClick={() => setGender("male")}
                    className={`px-3 py-1 rounded-lg text-xs transition-all ${
                      gender === "male"
                        ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                        : "bg-slate-800 text-slate-400 border border-slate-700 hover:text-white"
                    }`}
                  >
                    ♂ 男性
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">角色名称 *</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="例如：小樱"
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-rose-500/50"
                />
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1.5">角色描述 *</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="用一句话描述这个角色..."
                  rows={2}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-rose-500/50 resize-none"
                />
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-2">角色分类</label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setCategory(cat.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs transition-all ${
                        category === cat.id
                          ? "bg-rose-500 text-white"
                          : "bg-slate-800 text-slate-400 hover:text-white border border-slate-700"
                      }`}
                    >
                      {cat.emoji} {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* NSFW 开关 */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-slate-800/50 border border-slate-700/50">
                <div className="flex items-center gap-2">
                  {nsfw ? (
                    <ShieldOff className="w-4 h-4 text-rose-400" />
                  ) : (
                    <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  )}
                  <div>
                    <span className="text-xs text-slate-200 font-medium">内容标记</span>
                    <p className="text-[10px] text-slate-500">NSFW 角色仅在开启 NSFW 开关时显示</p>
                  </div>
                </div>
                <button
                  onClick={() => setNsfw(!nsfw)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    nsfw ? "bg-rose-500" : "bg-slate-700"
                  }`}
                >
                  <motion.div
                    animate={{ x: nsfw ? 20 : 2 }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    className="absolute top-1 w-4 h-4 rounded-full bg-white shadow"
                  />
                </button>
              </div>
            </div>
          </section>

          {/* 角色设定 */}
          <section className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
            <h2 className="text-sm font-semibold text-slate-200 mb-4">角色设定</h2>

            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs text-slate-400">性格特点</label>
                  <div className="flex gap-1 flex-wrap">
                    {PERSONALITY_PRESETS.map((p) => (
                      <button
                        key={p.label}
                        onClick={() => setPersonality(p.value)}
                        className="px-2 py-0.5 rounded text-[10px] bg-slate-800 text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea
                  value={personality}
                  onChange={(e) => setPersonality(e.target.value)}
                  placeholder="描述角色的性格，用顿号分隔..."
                  rows={2}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-rose-500/50 resize-none"
                />
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1.5">角色背景</label>
                <textarea
                  value={background}
                  onChange={(e) => setBackground(e.target.value)}
                  placeholder="角色的背景故事..."
                  rows={3}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-rose-500/50 resize-none"
                />
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1.5">开场场景</label>
                <textarea
                  value={scenario}
                  onChange={(e) => setScenario(e.target.value)}
                  placeholder="用户和角色相遇的场景设定..."
                  rows={2}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-rose-500/50 resize-none"
                />
              </div>
            </div>
          </section>

          {/* 系统提示词 */}
          <section className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
            <h2 className="text-sm font-semibold text-slate-200 mb-1">系统提示词 <span className="text-[10px] text-slate-500 font-normal ml-2">高级设定，覆盖默认行为</span></h2>
            <textarea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              placeholder="定义角色的核心行为准则（留空则自动生成）..."
              rows={4}
              className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-rose-500/50 resize-none font-mono"
            />
          </section>

          {/* 对话设定 */}
          <section className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
            <h2 className="text-sm font-semibold text-slate-200 mb-4">对话设定</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">开场白</label>
                <textarea
                  value={greeting}
                  onChange={(e) => setGreeting(e.target.value)}
                  placeholder="角色第一次见到用户时说的话...（可用 *包裹动作*）"
                  rows={3}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-rose-500/50 resize-none"
                />
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1.5">标签 <span className="text-slate-600">{tags.length}/10</span></label>
                <div className="flex gap-2">
                  <input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddTag())}
                    placeholder="输入标签后按回车"
                    className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-rose-500/50"
                  />
                  <button onClick={() => handleAddTag()} className="px-4 py-2 rounded-xl bg-slate-800 border border-slate-700 text-slate-400 hover:text-white text-sm">
                    添加
                  </button>
                </div>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-800 text-xs text-slate-300"
                      >
                        {tag}
                        <button onClick={() => setTags(tags.filter((t) => t !== tag))} className="text-slate-500 hover:text-red-400">×</button>
                      </span>
                    ))}
                  </div>
                )}
                {suggestedTags.length > 0 && tags.length < 10 && (
                  <div className="mt-2">
                    <p className="text-[10px] text-slate-600 mb-1">常用标签（点击添加）</p>
                    <div className="flex flex-wrap gap-1">
                      {suggestedTags.slice(0, 30).map((tag) => (
                        <button
                          key={tag}
                          onClick={() => handleAddTag(tag)}
                          className="px-2 py-0.5 rounded text-[10px] bg-slate-800/50 text-slate-500 hover:text-purple-400 hover:bg-purple-500/10 transition-colors border border-transparent hover:border-purple-500/20"
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Phase 2: 性格微调 */}
          <section className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
            <h2 className="text-sm font-semibold text-slate-200 mb-4">性格微调 <span className="text-[10px] text-slate-500 ml-2">影响角色言行风格</span></h2>
            <div className="space-y-3">
              {[
                { key: "shyness",      label: "害羞",   left: "大胆",     right: "害羞" },
                { key: "warmth",        label: "温暖",   left: "冷淡",     right: "温暖" },
                { key: "assertiveness", label: "主动",   left: "被动",     right: "主动" },
                { key: "jealousy",      label: "嫉妒心", left: "不计较",   right: "爱吃醋" },
                { key: "humor",         label: "幽默",   left: "严肃",     right: "幽默" },
                { key: "formality",     label: "正式",   left: "随意口语", right: "正式礼貌" },
              ].map(({ key, label, left, right }) => (
                <div key={key}>
                  <div className="flex justify-between text-[11px] text-slate-400 mb-1">
                    <span>{left}</span>
                    <span className="text-rose-400 font-medium">{label}: {traits[key as keyof typeof traits]}</span>
                    <span>{right}</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={traits[key as keyof typeof traits]}
                    onChange={(e) => setTraits({ ...traits, [key]: Number(e.target.value) })}
                    className="w-full accent-rose-500 h-1.5"
                  />
                </div>
              ))}
            </div>
          </section>

          {/* 保存按钮 */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSave}
            disabled={saving}
            className={`w-full py-3 rounded-xl font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2 ${
              isEdit
                ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700"
                : "bg-gradient-to-r from-rose-500 to-purple-600 text-white hover:from-rose-600 hover:to-purple-700"
            }`}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? (isEdit ? "保存中..." : "创建中...") : (isEdit ? "保存修改" : "创建角色")}
          </motion.button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
